import { useFormik } from "formik";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TAB_NAMES } from "src/apps/common/menu-navigation/menuNavigation";
import TabHeader from "src/apps/common/tab-header/TabHeader";
import Button from "src/components/buttons/Button";
import DatePicker from "src/components/calendar/DatePicker";
import Input from "src/components/inputs/Input";
import Toggle from "src/components/toggles/Toggle";
import useNotification from "src/hooks/useNotification";
import useSetActiveTab from "src/hooks/useSetActiveTab";
import { number, object, string } from "yup";
import {
  useCreateCoupon,
  useGetCouponData,
  useUpdateCoupon,
} from "../api-clients";

const FORM_FIELDS = {
  NAME: "name",
  DISCOUNT: "discount",
  MAX_DISCOUNT: "maxDiscount",
  START_DATE: "startDate",
  END_DATE: "endDate",
  STATUS: "status",
};

const CreateCoupon = ({ isInViewMode = false }: { isInViewMode?: boolean }) => {
  useSetActiveTab(TAB_NAMES.COUPON_MANAGEMENT);
  const navigate = useNavigate();
  const { couponId: id } = useParams();

  const couponId = useMemo(() => Number(id), [id]);

  const isInEditMode = useMemo(() => !!couponId, [couponId]);

  const { errorNotification, successNotification } = useNotification();

  const { isLoading: isGetCourseLoading, data } = useGetCouponData(couponId);

  const courseData = useMemo(() => data?.data ?? {}, [data?.data]);

  const {
    isLoading: isUpdateLoading,
    execute: updateCourse,
    isSuccess: isUpdateSuccess,
    error: isUpdateError,
  } = useUpdateCoupon(couponId);

  const { isLoading, isSuccess, execute, error } = useCreateCoupon();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      [FORM_FIELDS.NAME]: courseData?.[FORM_FIELDS.NAME] ?? "",
      [FORM_FIELDS.DISCOUNT]: courseData?.[FORM_FIELDS.DISCOUNT] ?? null,
      [FORM_FIELDS.MAX_DISCOUNT]:
        courseData?.[FORM_FIELDS.MAX_DISCOUNT] ?? null,
      [FORM_FIELDS.START_DATE]: courseData?.[FORM_FIELDS.START_DATE] ?? "",
      [FORM_FIELDS.END_DATE]: courseData?.[FORM_FIELDS.END_DATE] ?? "",
      [FORM_FIELDS.STATUS]: courseData?.[FORM_FIELDS.STATUS] ?? false,
    },
    validationSchema: object({
      [FORM_FIELDS.NAME]: string().required("This is a Required field."),
      [FORM_FIELDS.DISCOUNT]: number().required("This is a Required field."),
      // [FORM_FIELDS.MAX_DISCOUNT]: number().required(
      //   "This is a Required field."
      // ),
      [FORM_FIELDS.START_DATE]: string().required("This is a Required field."),
      [FORM_FIELDS.END_DATE]: string().required("This is a Required field."),
    }),
    onSubmit: (data) => {
      if (isInEditMode) {
        updateCourse(data);
        return;
      }
      execute(data);
    },
  });

  const { values, errors, handleSubmit, setFieldValue, touched } = formik;

  useEffect(() => {
    if (isSuccess || isUpdateSuccess) {
      successNotification(
        isUpdateSuccess ? "Coupon Updated" : "Coupon Created."
      );
      navigate("/store/coupons");
    }
  }, [isSuccess, isUpdateSuccess, navigate, successNotification]);

  useEffect(() => {
    if (error || isUpdateError) {
      errorNotification();
    }
  }, [error, errorNotification, isUpdateError]);

  const isInLoading = useMemo(
    () => isLoading || isGetCourseLoading || isUpdateLoading,
    [isGetCourseLoading, isLoading, isUpdateLoading]
  );

  const isFieldDisabled = useMemo(
    () => isInViewMode || isInLoading,
    [isInLoading, isInViewMode]
  );

  const getFieldError = useCallback(
    (fieldName: string) => {
      return touched[fieldName] ? (errors[fieldName] as string) : undefined;
    },
    [errors, touched]
  );

  return (
    <div className="flex flex-col gap-[20px] h-full">
      <TabHeader label="Add Coupon" showBreadcrumb />

      <div className="flex flex-col justify-between gap-6 bg-white rounded-md py-[30px] px-[42px] h-fit">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Coupon Name"
            placeholder="Coupon Name"
            name={FORM_FIELDS.NAME}
            value={values[FORM_FIELDS.NAME] as string}
            error={getFieldError(FORM_FIELDS.NAME)}
            onChange={(e) => setFieldValue(FORM_FIELDS.NAME, e.target.value)}
            required
            disabled={isFieldDisabled}
          />

          <DatePicker
            label="Coupon Start"
            value={values[FORM_FIELDS.START_DATE]}
            onChange={(value) => setFieldValue(FORM_FIELDS.START_DATE, value)}
            disabled={isFieldDisabled}
            required
          />

          <DatePicker
            label="Coupon End"
            value={values[FORM_FIELDS.END_DATE]}
            onChange={(value) => setFieldValue(FORM_FIELDS.END_DATE, value)}
            disabled={isFieldDisabled}
            required
          />

          <Input
            type="number"
            label="Discount"
            placeholder="Discount"
            name={FORM_FIELDS.DISCOUNT}
            value={values[FORM_FIELDS.DISCOUNT] || ""}
            error={getFieldError(FORM_FIELDS.DISCOUNT)}
            onChange={({ target: { value } }) =>
              setFieldValue(FORM_FIELDS.DISCOUNT, value ? Number(value) : "")
            }
            disabled={isFieldDisabled}
            rightIcon={() => (
              <div className="h-full border-l-[1px] flex justify-center items-center p-2 absolute right-0 text-gray-suva text-14px">
                %
              </div>
            )}
            required
          />

          <Input
            type="number"
            label="Maximum Discount"
            placeholder="Maximum Discount"
            name={FORM_FIELDS.MAX_DISCOUNT}
            value={values[FORM_FIELDS.MAX_DISCOUNT] || ""}
            error={getFieldError(FORM_FIELDS.MAX_DISCOUNT)}
            onChange={(e) =>
              setFieldValue(FORM_FIELDS.MAX_DISCOUNT, e.target.value)
            }
            disabled={isFieldDisabled}
            rightIcon={() => (
              <div className="h-full border-l-[1px] flex justify-center items-center p-2 absolute right-0 text-gray-suva text-14px">
                %
              </div>
            )}
          />

          <Toggle
            label="Status"
            checked={values[FORM_FIELDS.STATUS] as boolean}
            onToggle={(val) => setFieldValue(FORM_FIELDS.STATUS, val)}
            disabled={isFieldDisabled}
            showToggleStatus
          />
        </div>

        <Button
          text={isLoading ? "Loading..." : "Submit"}
          onClick={handleSubmit}
          primary
          className="h-[40px] px-10"
          isDisabled={isFieldDisabled}
        />
      </div>
    </div>
  );
};

export default CreateCoupon;
